// SPDX-License-Identifier: Apache-2.0
import LayerWms from '../../models/layers/layerwms';
import { LayerAttribute } from '../../models/serverwfs';
import {
  AttributeTypeGroup,
  filterOperators,
  LogicalFilterOperator,
  logicalFilterOperators
} from '../../models/filter';
import { v4 as uuidv4 } from 'uuid';
import IGirafeContext from '../../tools/context/icontext';
import WfsFilterCondition from '../../tools/wfs/wfsfiltercondition';
import WfsFilter from '../../tools/wfs/wfsfilter';
import ColumnAliasHelper from '../../tools/utils/aliases';
import { typeGroupFromAttributeType as xmlTypeGroupByAttributeType } from '../../models/xmlTypes';
import { UsedInTemplateOnly } from '../../decorators';
import FilterConditionElement from './filterconditionelement';

/**
 * Represents the state of a filter element in the AdvancedFilterComponent. This class provides functionality
 * to store and manage filter conditions, operators, and attributes.
 */
class LayerFilterElement {
  public id: string;
  public layer?: LayerWms;
  public attributes: LayerAttribute[];
  public conditions: FilterConditionElement[];
  public inverted: boolean = false;
  public attributeChoices: Map<string, string[]> = new Map();
  public attributeTitles: Map<string, string> = new Map();

  private readonly context: IGirafeContext;
  private readonly columnAliasHelper: ColumnAliasHelper;
  private readonly position: number;

  constructor(context: IGirafeContext, columnAliasHelper: ColumnAliasHelper, position: number) {
    this.context = context;
    this.columnAliasHelper = columnAliasHelper;
    this.id = uuidv4();
    this.position = position;
    this.attributes = [];
    this.conditions = [];
  }

  /**
   * Sets the filter element with all conditions from a given layer with an existing filter.
   */
  public async setFromLayer(layer: LayerWms) {
    await this.setLayer(layer);
    if (!layer.filter) {
      return;
    }
    this.conditions = [];
    this.setConditionsFromLayerRecursively(layer.filter);
  }

  private setConditionsFromLayerRecursively(filterObject: WfsFilter) {
    if (filterObject.logicalOperator === 'not') {
      this.inverted = true;
    }
    filterObject.conditions.forEach((nestedFilterCondition: WfsFilterCondition | WfsFilter) => {
      if (nestedFilterCondition instanceof WfsFilter) {
        this.setConditionsFromLayerRecursively(nestedFilterCondition);
      } else {
        this.addCondition();
        const cond = this.conditions.at(-1)!;
        this.setAttributeInCondition(cond, nestedFilterCondition.property);
        cond.setOperator(nestedFilterCondition.operator);
        cond.value = nestedFilterCondition.value;
        cond.value2 = nestedFilterCondition.value2;
        cond.logicalOperator = filterObject.logicalOperator;
        cond.filterConditionInstance = nestedFilterCondition;
        cond.applied = true;
      }
    });
  }

  public get title(): string {
    const title = `${this.i18n('Filter')} ${this.position + 1}`;
    if (this.layer) {
      return `${title} (${this.i18n(this.layer.name)})`;
    } else {
      return title;
    }
  }

  public get availableLogicalOperators(): {
    operator: LogicalFilterOperator;
    displayName: string;
  }[] {
    // Filter out 'not' logical operator, as it is only available as a dedicated button to negate the whole chained expression
    return [...logicalFilterOperators].filter((op) => op.operator !== 'not');
  }

  public async setLayer(layer: LayerWms) {
    this.attributes = [];
    this.attributeChoices = new Map();
    this.attributeTitles = new Map();
    this.inverted = false;
    this.removeConditions();

    if (layer) {
      this.layer = layer;
      this.attributes = await this.context.wfsManager.commonAttributesByLayer(layer, true);
      const queryLayer = layer.queryLayers?.split(',')[0] ?? '';
      for (const attribute of this.attributes) {
        this.attributeTitles.set(attribute.name, this.columnAliasHelper.getColumnAlias(queryLayer, attribute.name));
      }
      this.attributeChoices = await this.context.wfsManager.getAttributeChoices(layer);
    }
    // Add an empty condition
    this.addCondition();
  }

  public removeConditions() {
    for (const condition of this.conditions) {
      this.removeCondition(condition);
    }
  }

  public addCondition() {
    this.conditions.push(new FilterConditionElement(this.context));
  }

  public removeCondition(condition: FilterConditionElement) {
    const idx = this.conditions.indexOf(condition);
    if (idx !== -1) {
      condition.remove();
      this.conditions.splice(idx, 1);
    }
    if (this.conditions.length > 0) {
      // Remove the logical operator from the first condition in the list
      this.conditions[0].logicalOperator = undefined;
    }
  }

  public hasAppliedConditions(): boolean {
    return this.conditions.some((condition) => condition.applied);
  }

  public hasNoneAppliedConditions(): boolean {
    return this.conditions.some((condition) => !condition.applied);
  }

  public removeNonAppliedConditions() {
    for (const condition of this.conditions) {
      if (!condition.applied) {
        this.removeCondition(condition);
      }
    }
  }

  public setAttributeInCondition(condition: FilterConditionElement, attributeName: string) {
    const attribute = this.attributes.find((attr) => attr.name === attributeName);
    if (!attribute) {
      return;
    }
    const attributeDisplayName = this.getAttributeDisplayName(attribute);
    const attributeInputType = this.getInputTypeByAttributeType(attribute);
    let availableOperators = this.getOperatorsByAttributeType(attribute?.type);
    let choices: string[] = [];
    if (this.isEnumeratedAttribute(attributeName)) {
      availableOperators = filterOperators.filter((op) => ['in', 'nin', 'nul', 'nnul'].includes(op.operator));
      choices = this.attributeChoices.get(attributeName) ?? [];
    }
    condition.setAttribute(attribute, attributeDisplayName, attributeInputType, availableOperators, choices);
  }

  private isEnumeratedAttribute(attributeName: string) {
    return this.attributeChoices.has(attributeName) && this.attributeChoices.get(attributeName)?.length;
  }

  private getOperatorsByAttributeType(attributeType: string | undefined) {
    if (!attributeType) {
      return [];
    }
    return WfsFilterCondition.supportedOperatorsByAttributeType(attributeType);
  }

  public applyFilter(): WfsFilter | undefined {
    let nestedFilter: WfsFilter | undefined = undefined;
    if (!this.layer) {
      return nestedFilter;
    }
    for (const condition of this.conditions) {
      if (!condition.applied) {
        continue;
      }
      if (nestedFilter) {
        condition.logicalOperator = condition.logicalOperator ?? this.availableLogicalOperators[0].operator;
        nestedFilter = new WfsFilter(
          [nestedFilter, condition.filterConditionInstance as WfsFilterCondition],
          condition.logicalOperator
        );
      } else {
        nestedFilter = new WfsFilter([condition.filterConditionInstance as WfsFilterCondition]);
      }
    }
    if (nestedFilter && this.inverted) {
      nestedFilter = new WfsFilter([nestedFilter], 'not');
    }
    return nestedFilter;
  }

  public toggleInverted() {
    this.inverted = !this.inverted;
  }

  public i18n(key: string) {
    return this.context.i18nManager.getTranslation(key);
  }

  @UsedInTemplateOnly()
  public isLogicalOperatorSelectorVisible(condition: FilterConditionElement): boolean {
    // Show a logical operator for all conditions but the first
    return this.conditions.indexOf(condition) > 0; // NOSONAR: This check does exactly what it should do
  }

  private getInputTypeByAttributeType(attr: LayerAttribute): AttributeTypeGroup {
    if (!attr?.type) {
      return 'string';
    }
    if (this.layer instanceof LayerWms) {
      return xmlTypeGroupByAttributeType(attr.type) || 'string';
    }
    return 'string';
  }

  public getAttributeDisplayName(attribute: LayerAttribute) {
    const attributeName = this.attributeTitles.get(attribute.name) ?? attribute.name;
    const attrType = this.isEnumeratedAttribute(attribute.name) ? 'list' : attribute.type;
    return `${this.i18n(attributeName)} [${this.i18n('datatype-' + attrType)}]`;
  }
}

export default LayerFilterElement;
